import { alpha } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

export const useStyle = makeStyles<{ isDesktop: boolean; isActive?: boolean }>()((theme, { isDesktop, isActive }) => {
    return {
        appBar: {
            backgroundColor: theme.palette.background.light,
            boxShadow: theme.shadows[2],
            padding: theme.spacing(0, 4),
            position: "static",
            zIndex: 1001,
        },
        toolbar: {
            width: "100%",
            maxWidth: "1280px",
            margin: "0 auto",
            height: "66px",
            justifyContent: "space-between",
            [theme.breakpoints.down("md")]: {
                padding: theme.spacing(0, 1.5),
            },
        },
        logoContainer: {
            display: "flex",
            alignItems: "center",
            "& a": {
                display: "flex",
                alignItems: "center",
                "&:focus-visible": {
                    outline: `2px solid ${theme.palette.secondary.main}!important`,
                    outlineOffset: "2px",
                    borderRadius: "4px",
                },
            },
        },
        logo: {
            height: "auto",
            maxHeight: "46px",
        },
        desktopNav: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(4),
            flex: 1,
            justifyContent: "center",
            [theme.breakpoints.down("md")]: {
                display: "none",
            },
        },
        navLink: {
            color: theme.palette.secondary.main,
            textDecoration: "none",
            position: "relative",
            "&:hover": {
                "& .MuiTypography-root": {
                    fontWeight: theme.typography.fontWeightBold,
                },
                ...(isActive && {
                    "& .MuiTypography-root": {
                        fontWeight: theme.typography.fontWeightRegular,
                    },
                }),
            },
            "&:focus-visible": {
                fontWeight: theme.typography.fontWeightBold,
                outline: `2px solid ${theme.palette.secondary.main}`,
                outlineOffset: "2px",
                borderRadius: theme.shape.borderRadius,
                padding: theme.spacing(1.25, 1),
                ...(isActive && {
                    "& $navLinkLabel::after": {
                        bottom: "6px",
                        height: "4px",
                        left: "8px",
                        right: "8px",
                    },
                }),
            },
        },
        navLinkLabel: {
            position: "relative",
            display: "inline-block",
            ...(isActive && {
                "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: "-6px",
                    left: 0,
                    right: 0,
                    height: "4px",
                    backgroundColor: theme.palette.quaternary.main,
                },
            }),
        },
        navLinkText: {
            display: "flex",
            alignItems: "center",
            fontWeight: theme.typography.fontWeightMedium,
            gap: theme.spacing(0.5),
            ...(isActive && {
                fontWeight: theme.typography.fontWeightRegular,
            }),
        },
        newBadge: {
            display: "flex",
            alignItems: "center",
            backgroundColor: theme.palette.quaternary.main,
            color: theme.palette.secondary.main,
            padding: theme.spacing(0.5, 1.5),
            borderRadius: theme.shape.borderRadius,
            fontSize: 14,
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: "normal",
        },
        myAccountButton: {
            backgroundColor: theme.palette.quaternary.main,
            color: theme.palette.secondary.main,
            padding: theme.spacing(2, 3),
            fontWeight: theme.typography.fontWeightLight,
            textTransform: "none",
            height: "44px",
            transition: theme.transitions.create("background-color", {
                duration: theme.transitions.duration.shorter,
            }),
            "&&:hover": {
                // Unknown color from Figma
                backgroundColor: "#F5CC3B !important",
                boxShadow: "none !important",
            },
            "&:focus-visible": {
                outline: `2px solid ${theme.palette.secondary.main}!important`,
                outlineOffset: "2px",
            },
        },
        cartContainer: {
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            "&:focus-visible": {
                outline: `2px solid ${theme.palette.secondary.main}`,
                outlineOffset: "2px",
                borderRadius: "4px",
            },
        },
        loginContainer: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            cursor: "pointer",
            color: theme.palette.secondary.main,
            textDecoration: "none",
            backgroundColor: "transparent",
            borderRadius: "44px",
            padding: theme.spacing(1.5, 3),
            transition: theme.transitions.create("background-color", {
                duration: theme.transitions.duration.shorter,
            }),
            "&:hover": {
                backgroundColor: alpha(theme.palette.secondary.main, 0.05),
            },
            "&:focus-visible": {
                outline: `2px solid ${theme.palette.secondary.main}`,
                outlineOffset: "2px",
                borderRadius: "50px",
            },
        },
        mobileAccordion: {
            backgroundColor: theme.palette.background.light,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            boxShadow: theme.shadows[2],
            "&:before": {
                display: "none",
            },
            "&.Mui-expanded": {
                margin: 0,
            },
            ...(isDesktop && {
                display: "none",
            }),
        },
        accordionSummary: {
            minHeight: "72px",
            padding: theme.spacing(0, 2),
            "&.Mui-expanded": {
                minHeight: "72px",
            },
            "& .MuiAccordionSummary-content": {
                margin: 0,
                "&.Mui-expanded": {
                    margin: 0,
                },
            },
        },
        accordionHeader: {
            display: "flex",
            alignItems: "center",
            width: "100%",
            padding: theme.spacing(0, 1),
            position: "relative",
            minHeight: "72px",
        },
        accordionHeaderLeft: {
            display: "flex",
            alignItems: "center",
        },
        accordionHeaderCenter: {
            display: "flex",
            alignItems: "center",
            marginLeft: theme.spacing(3),
        },
        accordionHeaderRight: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(2),
            marginLeft: "auto",
        },
        accordionDetails: {
            padding: theme.spacing(1.5, 2),
            flexDirection: "column",
        },
        drawerCloseButton: {
            color: theme.palette.secondary.main,
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "44px",
            height: "44px",
            "& svg": {
                margin: "auto",
            },
        },
        drawerLogo: {
            height: "auto",
            maxHeight: "46px",
        },
        drawerMenuList: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
        },
        drawerMenuItem: {
            padding: theme.spacing(1.5),
            color: theme.palette.secondary.main,
            borderBottom: "none",
            borderRadius: theme.shape.borderRadius,
            "&:hover": {
                backgroundColor: theme.palette.background.dark,
                fontWeight: theme.typography.fontWeightBold,
            },
            ...(isActive && {
                fontWeight: theme.typography.fontWeightRegular,
            }),
        },
        drawerMyAccountButton: {
            width: "100%",
        },
        helpSubmenu: {
            backgroundColor: theme.palette.background.dark,
        },
        badge: {
            "& .MuiBadge-badge": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.light,
                fontSize: 12,
                top: 6,
                right: 4,
                minWidth: 0,
                width: 16,
                height: 16,
            },
        },
        desktopActionsContainer: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(6),
        },
        logoDesktop: {
            height: "46px",
            width: "80px",
        },
        logoMobile: {
            height: "46px",
            width: "80px",
        },
        myAccountButtonText: {
            fontWeight: theme.typography.fontWeightLight,
        },
        loginText: {
            color: theme.palette.secondary.main,
        },
        profileDesktopIcon: {
            width: "18px",
            height: "18px",
            color: theme.palette.secondary.main,
        },
        profileMobileIcon: {
            width: "28px",
            height: "28px",
            color: theme.palette.secondary.main,
        },
        cartIconDesktop: {
            color: theme.palette.secondary.main,
            height: "29px",
            width: "27px",
        },
        cartIconMobile: {
            color: theme.palette.secondary.main,
            height: "29px",
            width: "27px",
        },
        menuToggleButton: {
            width: "44px",
            height: "44px",
            padding: 0,
        },
        closeIcon: {
            width: "24px",
            height: "24px",
            color: theme.palette.secondary.main,
        },
        menuIcon: {
            width: "29px",
            height: "22px",
            color: theme.palette.secondary.main,
        },
        navLinkContainer: {
            display: "flex",
            alignItems: "center",
        },
        drawerMenuItemText: {
            color: theme.palette.secondary.main,
            fontWeight: theme.typography.fontWeightMedium,
            display: "flex",
            gap: theme.spacing(1),
        },
    };
});
